import { useState } from 'react';
import medicalVisaData from '@data/guides/medical-visa.json';

const MedicalVisaGuide = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Complete Guide to China Medical Visa
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          {medicalVisaData.summary}
        </p>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-primary-800">
            <strong>Last Updated:</strong> {medicalVisaData.lastUpdated}
          </p>
        </div>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Visa Type Comparison</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {medicalVisaData.visaTypes.map((visa) => (
            <div
              key={visa.id}
              className={`border-2 rounded-lg p-6 ${
                visa.id === 's2-medical'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              {visa.id === 's2-medical' && (
                <div className="inline-block bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  Recommended for Long-term Treatment
                </div>
              )}
              <h3 className="text-xl font-bold mb-4">{visa.name}</h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <span className="text-gray-600 w-24">Duration:</span>
                  <span className="font-medium">{visa.duration}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-600 w-24">Cost:</span>
                  <span className="font-medium">{visa.cost}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-600 w-24">For:</span>
                  <span className="font-medium">{visa.applicants}</span>
                </div>
              </div>
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Features:</h4>
                <ul className="space-y-1 text-sm">
                  {visa.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Restrictions:</h4>
                <ul className="space-y-1 text-sm">
                  {visa.restrictions.map((restriction, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-red-600 mr-2">✗</span>
                      <span>{restriction}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Best For:</h4>
                <ul className="space-y-1 text-sm">
                  {visa.recommendedFor.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Recommended Approach</h2>
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold mb-2">1</div>
              <h3 className="font-semibold mb-2">Initial Consultation</h3>
              <p className="text-sm">Use visa-free transit (6-10 days) for first visit and diagnosis</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold mb-2">2</div>
              <h3 className="font-semibold mb-2">Confirm Treatment</h3>
              <p className="text-sm">Apply for S2 visa (90-180 days) once treatment plan is confirmed</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="text-2xl font-bold mb-2">3</div>
              <h3 className="font-semibold mb-2">Long-term Treatment</h3>
              <p className="text-sm">Extend visa in China for up to 1 year if needed</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Application Process</h2>
        <div className="space-y-4">
          {medicalVisaData.applicationSteps.map((step) => (
            <div
              key={step.step}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setExpandedStep(expandedStep === step.step ? null : step.step)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold">
                    {step.step}
                  </div>
                  <div>
                    <h3 className="font-semibold">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedStep === step.step ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedStep === step.step && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-2">Required Documents:</h4>
                      <ul className="space-y-1 text-sm">
                        {step.requiredDocuments?.map((doc, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-primary-600 mr-2">•</span>
                            <span>{doc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Details:</h4>
                      <div className="space-y-2 text-sm">
                        {step.processingTime && (
                          <p><strong>Processing Time:</strong> {step.processingTime}</p>
                        )}
                        {step.contact && (
                          <p><strong>Contact:</strong> {step.contact}</p>
                        )}
                        {step.platform && (
                          <p><strong>Platform:</strong> {step.platform}</p>
                        )}
                        {step.timeline && (
                          <div className="mt-2">
                            <strong>Processing Times:</strong>
                            <ul className="mt-1 space-y-1">
                              {Object.entries(step.timeline).map(([key, value]) => (
                                <li key={key}>
                                  <strong>{key}:</strong> {value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {step.options && (
                          <div className="mt-2">
                            <strong>Options:</strong>
                            <ul className="mt-1 space-y-1">
                              {step.options.map((opt, idx) => (
                                <li key={idx}>{opt}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {step.locations && (
                          <div className="mt-2">
                            <strong>Locations:</strong>
                            <ul className="mt-1 space-y-1">
                              {step.locations.map((loc, idx) => (
                                <li key={idx}>{loc}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {step.tips && step.tips.length > 0 && (
                          <div className="mt-2">
                            <strong>Tips:</strong>
                            <ul className="mt-1 space-y-1">
                              {step.tips.map((tip, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="text-green-600 mr-2">✓</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {step.process && step.process.length > 0 && (
                          <div className="mt-2">
                            <strong>Process:</strong>
                            <ol className="mt-1 space-y-1 list-decimal list-inside">
                              {step.process.map((proc, idx) => (
                                <li key={idx}>{proc}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Patient Stories & Experiences</h2>
        <p className="text-gray-600 mb-6">
          Watch real patient experiences from international patients who received medical treatment in China. These videos provide first-hand insights into visa process, hospital visits, and quality of care.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {medicalVisaData.patientStories?.map((story) => (
            <div
              key={story.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="relative">
                <a
                  href={story.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src={story.thumbnail}
                    alt={story.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                    YouTube
                  </div>
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {story.duration}
                  </div>
                </a>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0v4a4 4 0 0011.366.873l4.515 4.515a4 4 0 001.366 1.366H19a4 4 0 0011.366-1.366l-4.515-4.515a4 4 0 00-1.366-1.366H5a4 4 0 00-1.366 1.366l-4.515 4.515a4 4 0 00-1.366-1.366z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{story.patient}</h3>
                    <p className="text-xs text-gray-600">{story.condition}</p>
                  </div>
                </div>
                <h4 className="font-bold text-lg mb-2 hover:text-primary-600">
                  <a
                    href={story.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {story.title}
                  </a>
                </h4>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {story.description}
                </p>
                <a
                  href={story.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-3 text-primary-600 font-medium hover:text-primary-700 text-sm"
                >
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385.846-1.426.821-2.087.325-2.087-2.097 0-1.794.617-3.097-1.65-3.097zm-8.936 7.268l-4.941 4.906c-.835.835-1.305 1.955-1.305 3.123v1.732c0 1.168.47 2.288 1.305 3.123l4.941 4.906c.835.835 2.065.806 3.232.806 1.169 0 2.289-.029 3.123-.806l4.941 4.906c-.835-.835-1.305 1.955-1.305 3.123v1.732c0-1.168-.47-2.288-1.305-3.123l-4.941-4.906c-.835-.835-2.065-.806-3.232-.806-1.169 0-2.289.029-3.123.806l-4.941 4.906z" />
                  </svg>
                  Watch on YouTube
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Required Documents by Category</h2>
        <div className="space-y-6">
          {medicalVisaData.requiredDocuments.map((category) => (
            <div key={category.category} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">{category.category}</h3>
              <div className="space-y-4">
                {category.items.map((item, idx) => (
                  <div key={idx} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        item.optional ? 'bg-gray-300 text-gray-600' : 'bg-primary-600 text-white'
                      }`}>
                        {item.optional ? '?' : '✓'}
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold">{item.name}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                      {'visaTypes' in item && (item as any).visaTypes && (
                        <p className="text-xs text-primary-600 mt-1">
                          Required for: {(item as any).visaTypes.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {medicalVisaData.faq.map((faq, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
                    {faq.category}
                  </span>
                  <span className="font-medium">{faq.question}</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${
                    expandedFaq === idx ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedFaq === idx && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default MedicalVisaGuide;
